import Icon, { ExclamationCircleFilled, LoadingOutlined } from '@ant-design/icons';
import type { InputRef } from 'antd';
import { Input, Upload, message } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { useEffect, useRef, useState } from 'react';

import SuccessIcon from '@/components/Icon/svgs/todo_done.svg';
import UploadIcon from '@/components/Icon/svgs/upload.svg';
import MindverseModal, { SizeType } from '@/components/modal';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import { type ISmartTopicListItem } from '@/service/smart-topic';
import useSmartTopicStore from '@/store/useSmartTopicStore';
import { handleTencentUpload } from '@/utils/upload';

import { defaultIcon } from '../topicCardItem/constant';
import styles from './index.less';

// 枚举类型,成功或者失败
enum UploadStatus {
  success = 'success',
  error = 'error',
  pending = 'pending'
}

interface IProps {
  open: boolean;
  onClose: () => void;
  type?: 'add' | 'edit';
  data?: ISmartTopicListItem;
}

export default function TopicModal(props: IProps): JSX.Element {
  const { onClose, open, data, type = 'add' } = props;
  const nameRef = useRef<InputRef>(null);
  const [name, setName] = useState(data?.name ?? '');
  const [description, setDescription] = useState(data?.description ?? '');
  const [icon, setIcon] = useState(data?.icon ?? '');
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>();
  const [layoutSize] = useStorage('layoutSize');
  const { t } = useTranslation();

  const addTopicItem = useSmartTopicStore((state) => state.addTopicItem);
  const updateTopicItem = useSmartTopicStore((state) => state.updateTopicItem);

  const closeModal = () => {
    setName('');
    setDescription('');
    setIcon('');
    setLoading(false);
    setUploadStatus(undefined);

    onClose();
  };

  const initModal = () => {
    setName(data?.name ?? '');
    setDescription(data?.description ?? '');
    setIcon(data?.icon ?? '');
  };

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        nameRef.current?.focus();
      }, 50);
      initModal();
    }
  }, [open]);

  const save = () => {
    if (type === 'add') {
      addTopicItem({ ...data, name, description, icon }, closeModal);
    } else if (type === 'edit') {
      if (!data?.id) {
        message.error('Oops, something went wrong');

        return;
      }

      updateTopicItem({ ...data, name, description, icon }, closeModal);
    }

    closeModal();
  };

  const doUploadFile = (file: any) => {
    const path = `smartTopic/icon`;
    const filePrefix = `${Date.now()}`;

    handleTencentUpload(file, path, filePrefix)
      .then((link) => {
        setLoading(false);
        setIcon(link);
        setUploadStatus(UploadStatus.success);
      })
      .catch(() => {
        setLoading(false);
        setUploadStatus(UploadStatus.error);
      });

    // handleUpload(file, path, filePrefix)
    //   .then((link) => {
    //     setLoading(false);
    //     setIcon(link);
    //     setUploadStatus(UploadStatus.success);
    //   })
    //   .catch(() => {
    //     setLoading(false);
    //     setUploadStatus(UploadStatus.error);
    //   });
  };

  return (
    <MindverseModal
      destroyOnClose={true}
      okButtonProps={{ disabled: !name }}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      onCancel={() => closeModal()}
      onOk={() => save()}
      open={open}
      size={SizeType.normal}
      title={type === 'add' ? t('topic.topicModal.addNewTopic') : t('topic.topicModal.editTopic')}
    >
      <div className="w-full flex flex-col gap-6">
        <Upload
          accept=".jpg,.jpeg,.png"
          className={layoutSize === LAYOUT_SIZE.SMALL ? styles.uploadContainer : ''}
          customRequest={(options) => {
            const newUploadFile: RcFile = options.file as RcFile;

            setLoading(true);
            (newUploadFile as any)['status'] = 'uploading';
            doUploadFile(newUploadFile);
          }}
          showUploadList={false}
        >
          <div className={styles.upload}>
            <div className={styles.uploadText}>{t('topic.topicModal.changeCover')}</div>

            {uploadStatus === UploadStatus.error && (
              <div className={styles.error}>
                <ExclamationCircleFilled />
                <span>{t('topic.topicModal.uploadFailed')}</span>
              </div>
            )}

            {uploadStatus === UploadStatus.success && (
              <div className={styles.success}>
                <Icon component={SuccessIcon} />
              </div>
            )}

            <div className={styles.uploadBtn}>
              {loading ? (
                <>
                  <div className={styles.loading}>
                    <LoadingOutlined />
                  </div>
                  <div className={styles.loadingCover} />
                </>
              ) : (
                <>
                  <Icon className={styles.uploadIcon} component={UploadIcon} />
                  <div className={styles.cover} />
                </>
              )}

              <img
                alt="cover"
                className="rounded-[8px]"
                height={48}
                src={icon ? icon : defaultIcon}
                width={48}
              />
            </div>
          </div>
        </Upload>

        <Input
          ref={nameRef}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('topic.topicModal.namePlaceholder')}
          value={name}
        />

        <Input
          onChange={(e) => setDescription(e.target.value)}
          placeholder={t('topic.topicModal.descriptionPlaceholder')}
          value={description}
        />
      </div>
    </MindverseModal>
  );
}
